import * as React from 'react';

import { Checkbox, Select } from '@components/common';
import {
  END_OF_SURVEY_ALLOWING_AUTO_REVIEW,
  isEndOfSurvey
} from '@components/GQSurveyBuilder/components/SkipLogicSlideOut';
import { QuestionSelect } from '@components/GQSurveyBuilder/components/SkipLogicSlideOut/QuestionSelect';
import { InfoCircleIcon } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';

import { OutcomeTagType, RuleOutcome as RuleOutcomeType } from '../utils';

interface OutcomeProps {
  question: ScreenerField;
  ruleOutcome: RuleOutcomeType;
  fields: ScreenerField[];
  allRuleOutcomes: RuleOutcomeType[];
  onUpdateRuleOutcome: (outcome: RuleOutcomeType) => void;
  allowedOutcomeTypes?: OutcomeTagType[];
}

export const RuleOutcome: React.FC<OutcomeProps> = ({
  question,
  ruleOutcome,
  fields,
  allRuleOutcomes,
  onUpdateRuleOutcome,
  allowedOutcomeTypes = ['skip_to', 'ideal', 'disqualifying'] as OutcomeTagType[]
}) => {
  const otherRuleOutcome: RuleOutcomeType = allRuleOutcomes.filter(
    (outcome) => outcome.tag_type !== ruleOutcome.tag_type
  )[0];

  return (
    <div className='space-y-4 rounded bg-gray-50 p-3'>
      <div className='flex items-end space-x-3'>
        <span className='mb-2 text-xs font-bold'>Then</span>
        <div className='flex-1'>
          <Select
            className='w-full bg-white'
            placeholder='Select option...'
            value={ruleOutcome.tag_type}
            options={[
              {
                label: 'Go To',
                value: 'skip_to',
                disabled: otherRuleOutcome?.tag_type === 'skip_to',
                disabledTooltip: 'Conditional logic has already been created for this rule.'
              },
              {
                label: 'Mark qualifying',
                value: 'ideal',
                disabled: otherRuleOutcome?.tag_type === 'disqualifying' || otherRuleOutcome?.tag_type === 'ideal',
                disabledTooltip:
                  'Qualifying or disqualifying answers have already been marked for conditions in this rule.'
              },
              {
                label: 'Mark disqualifying',
                value: 'disqualifying',
                disabled: otherRuleOutcome?.tag_type === 'disqualifying' || otherRuleOutcome?.tag_type === 'ideal',
                disabledTooltip:
                  'Qualifying or disqualifying answers have already been marked for conditions in this rule.'
              }
            ].filter((option) => allowedOutcomeTypes.includes(option.value as OutcomeTagType))}
            onChange={(selectedOption) => {
              onUpdateRuleOutcome({ tag_type: selectedOption as OutcomeTagType | '', tag_value: '' });
            }}
          />
        </div>
      </div>

      {ruleOutcome.tag_type === 'skip_to' && (
        <div>
          <QuestionSelect
            currentQuestion={question}
            questions={fields}
            value={ruleOutcome.tag_value as SkipLogic['to']}
            onChange={(to) => {
              onUpdateRuleOutcome({ tag_type: ruleOutcome.tag_type, tag_value: to as SkipLogic['to'] });
            }}
            endValue={
              isEndOfSurvey(ruleOutcome.tag_value as SkipLogic['to'])
                ? (ruleOutcome.tag_value as SkipLogic['to'])
                : END_OF_SURVEY_ALLOWING_AUTO_REVIEW
            }
          />
        </div>
      )}

      {ruleOutcome.tag_type === 'disqualifying' && (
        <div className='mt-2 flex items-center text-xs'>
          <Checkbox
            id={`dont-store-responses-${question.id}`}
            name={`dont-store-responses-${question.id}`}
            onChange={() => {
              onUpdateRuleOutcome({
                ...ruleOutcome,
                ignore_disqualified: !ruleOutcome.ignore_disqualified
              });
            }}
            selected={!!ruleOutcome.ignore_disqualified}
          />
          <label className='ml-2 cursor-pointer text-xs' htmlFor={`dont-store-responses-${question.id}`}>
            Don&apos;t store participant responses
          </label>
          <Tooltip content='New participant data, responses, and personal information will not be stored. Those who already exist in your system will be removed from study consideration.'>
            <InfoCircleIcon className='ml-2 h-4 w-4' />
          </Tooltip>
        </div>
      )}
    </div>
  );
};
